import { Text } from '@noorden-insurtech/alba-design-system';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import type { PmePremiumCardView } from './map-policy-to-pme-view.util';

type Props = {
  data: PmePremiumCardView;
  defaultExpanded?: boolean;
};

/** Linha 1 do mockup: rótulo pequeno claro sobre valor em negrito. */
function TopField({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex min-w-0 flex-col items-start'>
      <Text className='text-xs text-alba-white-500/80'>{label}</Text>
      <Text className='truncate text-sm font-bold text-alba-white-500'>
        {value}
      </Text>
    </div>
  );
}

/** Linha 2 do mockup: a hierarquia se inverte — rótulo negrito, valor claro. */
function BottomField({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex min-w-0 flex-col items-start'>
      <Text className='text-sm font-bold text-alba-white-500'>{label}</Text>
      <Text className='truncate text-xs text-alba-white-500/80'>{value}</Text>
    </div>
  );
}

/**
 * Card de prêmio da apólice PME. Diferente do card do Capital Global em dois
 * pontos que o mockup deixa explícitos: o valor é o prêmio MENSAL e a segunda
 * linha inverte a tipografia (rótulo em negrito, valor em cinza claro).
 */
export function PmePolicyPremiumCard({ data, defaultExpanded = true }: Props) {
  const [collapsed, setCollapsed] = useState(!defaultExpanded);

  return (
    <div className='flex w-full'>
      <div className='relative flex w-full flex-col items-center self-stretch rounded-2xl bg-linear-to-br from-[#2C285F] to-[#18163D] px-6 py-6 text-center'>
        <button
          type='button'
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expandir resumo' : 'Recolher resumo'}
          onClick={() => setCollapsed(!collapsed)}
          className='absolute top-0 right-0 cursor-pointer border-none bg-transparent px-6 py-4'
        >
          {collapsed ? (
            <ChevronDown className='size-6 text-alba-white-500' />
          ) : (
            <ChevronUp className='size-6 text-alba-white-500' />
          )}
        </button>

        <Text className='mb-2 text-sm text-alba-white-500'>
          Prêmio Mensal Total
        </Text>
        <Text className='text-3xl leading-[44px] font-bold text-alba-white-500'>
          {data.totalValue}
        </Text>

        <div className='mt-4 grid w-full grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4 sm:gap-x-3'>
          <TopField label='Prêmio Liquido' value={data.liquidValue} />
          <TopField label='IOF' value={data.iofValue} />
          <TopField label='Índice' value={data.index} />
          <TopField
            label='Periodicidade de pagamento'
            value={data.paymentFrequency}
          />
        </div>

        {!collapsed && (
          <div className='mt-4 grid w-full grid-cols-2 gap-x-6 gap-y-4 border-t border-[#9FA4A6] pt-4 sm:grid-cols-4 sm:gap-x-3'>
            <BottomField label='Produto' value={data.product} />
            <BottomField label='Número da Cotação' value={data.quoteNumber} />
            <BottomField label='Dia de vencimento:' value={data.dueDay} />
            <BottomField label='Status' value={data.status} />
          </div>
        )}
      </div>
    </div>
  );
}
